import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Sparkles, Crown, ShieldCheck, Users, TrendingUp, Gift, Zap,
  Award, Globe, Mic, Image as ImageIcon, FileText, Trophy, ArrowRight, Star, Lock,
  ArrowLeft, Home,
} from "lucide-react";
import SeoHead from "../components/SeoHead";

const TIERS = [
  {
    key: "advocate",
    name: "Advocate",
    icon: Sparkles,
    headline: "Free to join. Start earning today.",
    perks: [
      "Personal referral link",
      "Earn on every practitioner subscription",
      "CAUL rewards for content + reviews",
      "Public profile flair",
    ],
    threshold: "0 referrals",
  },
  {
    key: "ambassador",
    name: "Ambassador",
    icon: ShieldCheck,
    headline: "10+ active practitioners",
    perks: [
      "Higher per-sub commission",
      "Verified Ambassador badge",
      "Exclusive Cauldron merch drops",
      "Private Discord access",
    ],
    threshold: "10 referrals",
  },
  {
    key: "elite",
    name: "Elite Ambassador",
    icon: Crown,
    headline: "50+ practitioners. Real revenue share.",
    perks: [
      "Top-tier commission",
      "Featured placement across the app",
      "Direct line to Cauldron leadership",
      "First access to new features",
    ],
    threshold: "50 referrals",
    featured: true,
  },
  {
    key: "regional",
    name: "Regional Director",
    icon: Globe,
    headline: "Run a region. Own a market.",
    perks: [
      "Override commissions on your downline",
      "Host live events / webinars",
      "Build local ambassador chapters",
      "Revenue share on regional bookings",
    ],
    threshold: "250 referrals",
  },
];

const TARGETS = [
  { label: "Reiki Masters & Energy Healers", icon: "✦" },
  { label: "Ifá Community — Babaláwos, Iyánífás, Ìṣẹ̀ṣe", icon: "𓋹" },
  { label: "Voodoo — Bokors, Houngans, Mambos", icon: "⚜" },
  { label: "Palo Mayombe — Tata & Yaya Nganga", icon: "⊕" },
  { label: "Hoodoo Practitioners & Rootworkers", icon: "✺" },
  { label: "Tarot Readers, Astrologers, Mediums", icon: "✷" },
  { label: "Psychics & Spiritual Coaches", icon: "✸" },
  { label: "Holistic Healers & Crystal Workers", icon: "✤" },
];

function formatUsd(cents) { return `$${(cents / 100).toFixed(2)}`; }

export default function Ambassadors() {
  const { user } = useAuth();
  const homeHref = user ? "/feed" : "/";
  const homeLabel = user ? "Back to Feed" : "Back to Home";
  const [config, setConfig] = useState(null);
  const [foundingStats, setFoundingStats] = useState(null);
  const [calc, setCalc] = useState({ referrals: 100, tierKey: "elite" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, fs] = await Promise.all([
          api.get("/ambassador/program-config"),
          api.get("/ambassador/founding-stats"),
        ]);
        if (!cancelled) {
          setConfig(c.data);
          setFoundingStats(fs.data);
        }
      } catch { /* fail silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const tierRateCents = config?.commissionUsdCentsByTier?.[calc.tierKey] || 500;
  const monthlyUsd = (calc.referrals * tierRateCents / 100).toFixed(2);
  const annualUsd = ((calc.referrals * tierRateCents * 12) / 100).toFixed(2);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="ambassadors-landing">
      <SeoHead
        title="Cauldron Ambassador Program — Recurring Income for Spiritual Practitioners"
        description="Promote Cauldron, recruit practitioners, earn recurring USD + CAUL rewards. Founding Ambassadors get lifetime higher commissions."
        keywords="cauldron ambassador, spiritual affiliate program, ifa referral, reiki affiliate, hoodoo, palo mayombe, voodoo affiliate, spiritual recurring income"
      />

      {/* ─── Top nav back-link ─── */}
      <div className="max-w-6xl mx-auto px-6 pt-5">
        <Link
          to={homeHref}
          className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-secondary transition-colors"
          data-testid="ambassador-back-link"
        >
          {user ? <Home size={14} /> : <ArrowLeft size={14} />}
          {homeLabel}
        </Link>
      </div>

      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden border-b border-secondary/15">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          background: "radial-gradient(ellipse at top, rgba(212,181,140,0.6), transparent 60%)",
        }} />
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28 relative">
          <div className="label-mystic text-secondary/85 mb-6" data-testid="ambassadors-eyebrow">
            ⚱ Cauldron Ambassador Program · Apply Today
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            Build the future of spiritual practice.
            <br />
            <span className="text-secondary">Earn recurring income doing it.</span>
          </h1>
          <p className="text-foreground/75 text-lg sm:text-xl leading-relaxed mt-8 max-w-2xl">
            Cauldron Ambassadors recruit practitioners, host events, and grow the
            sacred marketplace. In return, they earn recurring USD on every
            subscription, CAUL rewards on every action, and lifetime status as
            architects of the lineage.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
            <Link
              to="/ambassador/signup"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold transition inline-flex items-center gap-2"
              data-testid="hero-signup-btn"
            >
              Sign Up Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/ambassadors/apply"
              className="border border-secondary/45 text-secondary/90 hover:bg-secondary/10 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs transition"
              data-testid="hero-apply-btn"
            >
              Or apply for Founding →
            </Link>
            <Link
              to="/ambassadors/leaderboard"
              className="border border-secondary/45 text-secondary/90 hover:bg-secondary/10 px-8 py-4 rounded-sm uppercase tracking-[0.18em] text-xs transition"
            >
              Top ambassadors →
            </Link>
          </div>

          {foundingStats && foundingStats.isOpen && (
            <div className="mt-12 max-w-xl bg-secondary/[0.08] border border-secondary/30 p-5 rounded-sm" data-testid="founding-counter">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-secondary" />
                <div className="label-mystic text-secondary">Founding Ambassadors — Limited to {foundingStats.max}</div>
              </div>
              <div className="font-serif text-3xl text-foreground">
                {foundingStats.remaining} <span className="text-foreground/55 text-lg">of {foundingStats.max} spots remaining</span>
              </div>
              <div className="mt-3 h-1.5 bg-foreground/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-secondary"
                  style={{ width: `${(foundingStats.claimed / foundingStats.max) * 100}%` }}
                />
              </div>
              <p className="text-xs text-foreground/70 mt-3 leading-relaxed">
                Founding members get a permanent badge, lifetime higher-commission rates,
                a founder NFT (Phase 3), and recognition as architects of the platform.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ─── WHY / Value Props ─── */}
      <section className="border-b border-secondary/15">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="label-mystic text-foreground/60 mb-3">Why Cauldron Ambassadors</div>
          <h2 className="font-serif text-4xl sm:text-5xl max-w-3xl">
            Not an influencer program. <span className="text-secondary">A leadership program.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: TrendingUp, title: "Recurring revenue", body: "Earn $5–$24.99 every month, for every practitioner you bring on, for as long as they stay subscribed. 100 practitioners = $500–$2,499/mo passive." },
              { icon: Gift, title: "CAUL rewards", body: "On top of USD: CAUL tokens for content, reviews, social shares, and recruiting. Tier-up bonuses up to 100,000 CAUL." },
              { icon: Award, title: "Status & ownership", body: "Verified badge, featured placement, leadership group access. Founding 100 hold lifetime higher rates." },
              { icon: Mic, title: "Built-in training", body: "Monday growth calls, swipe copy, ready-made creative for every platform. We make it easy to promote." },
              { icon: Users, title: "Real community", body: "Private Discord, monthly competitions, top-recruiter prizes. Recognition is part of the comp plan." },
              { icon: Zap, title: "Founding NFT", body: "First 100 approved get a Founder badge — and a Founder NFT on XRPL in Phase 3 (commemorative on-chain proof of being early)." },
            ].map((v, i) => (
              <div key={i} className="parchment p-6 hover:border-secondary/45 transition" data-testid={`value-prop-${i}`}>
                <v.icon className="w-5 h-5 text-secondary" />
                <div className="font-serif text-xl mt-4">{v.title}</div>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TIER LADDER ─── */}
      <section className="border-b border-secondary/15">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="label-mystic text-foreground/60 mb-3">The Ladder</div>
          <h2 className="font-serif text-4xl sm:text-5xl">Four tiers. Real upside.</h2>
          <p className="text-foreground/70 mt-4 max-w-2xl">Promotion is automatic — every threshold crossed unlocks a new tier, a new commission rate, and a CAUL bonus.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {TIERS.map((t) => {
              const Icon = t.icon;
              const rateCents = config?.commissionUsdCentsByTier?.[t.key];
              return (
                <div
                  key={t.key}
                  className={`parchment p-6 relative ${t.featured ? "border-secondary/60 bg-secondary/[0.04]" : ""}`}
                  data-testid={`tier-card-${t.key}`}
                >
                  {t.featured && (
                    <div className="absolute -top-3 left-6 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm">
                      Most Popular
                    </div>
                  )}
                  <Icon className="w-6 h-6 text-secondary" />
                  <div className="font-serif text-2xl mt-3">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 mt-1">{t.threshold}</div>
                  <p className="text-sm text-foreground/75 mt-3 leading-relaxed">{t.headline}</p>
                  {rateCents && (
                    <div className="mt-5 border-t border-secondary/15 pt-4">
                      <div className="font-serif text-3xl text-secondary">{formatUsd(rateCents)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-foreground/55 mt-1">per practitioner sub / mo</div>
                    </div>
                  )}
                  <ul className="mt-5 space-y-2">
                    {t.perks.map((p) => (
                      <li key={p} className="text-xs text-foreground/75 flex items-start gap-2">
                        <Star className="w-3 h-3 text-secondary mt-1 flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── INCOME CALCULATOR ─── */}
      <section className="border-b border-secondary/15">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="label-mystic text-foreground/60 mb-3">Income Calculator</div>
          <h2 className="font-serif text-4xl sm:text-5xl">What could you earn?</h2>
          <p className="text-foreground/70 mt-4 max-w-2xl">
            Cauldron subscriptions are <strong>{config ? `$${config.subscriptionPriceUsd}` : "$49.99"}/mo</strong> per practitioner.
            Drag the slider — see what your community could pay you.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-12 items-start">
            <div className="md:col-span-2 space-y-6">
              <div>
                <div className="flex items-baseline justify-between">
                  <label className="label-mystic text-foreground/65">Active Practitioner Referrals</label>
                  <div className="font-serif text-3xl text-secondary" data-testid="calc-referrals">{calc.referrals}</div>
                </div>
                <input
                  type="range" min="1" max="500" step="1"
                  value={calc.referrals}
                  onChange={(e) => setCalc({ ...calc, referrals: parseInt(e.target.value) })}
                  className="w-full mt-3 accent-secondary"
                  data-testid="calc-referrals-slider"
                />
              </div>
              <div>
                <div className="label-mystic text-foreground/65 mb-3">Your Tier</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setCalc({ ...calc, tierKey: t.key })}
                      className={`px-3 py-2.5 rounded-sm border text-xs uppercase tracking-wider transition ${
                        calc.tierKey === t.key
                          ? "bg-secondary text-secondary-foreground border-secondary"
                          : "border-foreground/15 text-foreground/70 hover:border-secondary/50"
                      }`}
                      data-testid={`calc-tier-${t.key}`}
                    >
                      {t.name.split(" ")[0]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="parchment p-6 border-secondary/45 bg-secondary/[0.04]" data-testid="calc-results">
              <div className="label-mystic text-secondary mb-1">Per month</div>
              <div className="font-serif text-5xl text-foreground" data-testid="calc-monthly">${monthlyUsd}</div>
              <div className="mt-4 label-mystic text-foreground/60 mb-1">Per year</div>
              <div className="font-serif text-3xl text-secondary" data-testid="calc-annual">${annualUsd}</div>
              <p className="text-[11px] text-foreground/60 mt-5 leading-relaxed">
                Plus CAUL rewards on content, reviews, and tier-ups. This is the
                USD line only — actual earnings include CAUL.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHO WE WANT ─── */}
      <section className="border-b border-secondary/15">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="label-mystic text-foreground/60 mb-3">Who We&apos;re Looking For</div>
          <h2 className="font-serif text-4xl sm:text-5xl max-w-3xl">
            If you already lead a community, <span className="text-secondary">we want you.</span>
          </h2>
          <p className="text-foreground/70 mt-4 max-w-2xl">
            Cauldron is built for serious practitioners and the people who follow them.
            If any of these describe you or someone you know, we want to talk.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-10">
            {TARGETS.map((t, i) => (
              <div key={i} className="parchment p-4 flex items-center gap-3 hover:border-secondary/45 transition">
                <div className="text-2xl text-secondary font-serif w-8 text-center">{t.icon}</div>
                <div className="text-sm text-foreground/85">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MARKETING KIT TEASER ─── */}
      <section className="border-b border-secondary/15 bg-secondary/[0.03]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="label-mystic text-foreground/60 mb-3">Marketing Kit</div>
              <h2 className="font-serif text-4xl sm:text-5xl">We make promotion easy.</h2>
              <p className="text-foreground/75 mt-5 leading-relaxed">
                Every Ambassador gets the Cauldron Marketing Kit: pre-built creative for Facebook ads,
                Instagram posts, TikTok graphics, 30s + 60s video clips, and prewritten swipe copy for
                tweets, emails, and DMs. Plug in your link and post.
              </p>
              <Link
                to="/ambassadors/kit"
                className="mt-8 inline-flex items-center gap-2 text-secondary hover:underline uppercase tracking-wider text-sm"
                data-testid="kit-preview-link"
              >
                Preview the kit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: ImageIcon, label: "Social Graphics" },
                { icon: Mic, label: "Audio + Video Clips" },
                { icon: FileText, label: "Swipe Copy" },
                { icon: Trophy, label: "Live Event Decks" },
              ].map((k, i) => (
                <div key={i} className="parchment p-5 flex flex-col gap-3 hover:border-secondary/45 transition">
                  <k.icon className="w-5 h-5 text-secondary" />
                  <div className="font-serif text-base">{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="label-mystic text-secondary mb-4">Ready?</div>
          <h2 className="font-serif text-5xl sm:text-6xl">Apply to be a Cauldron Ambassador.</h2>
          <p className="text-foreground/75 text-lg mt-6 leading-relaxed">
            Every applicant is reviewed by hand within 3–5 business days. The first 100
            approved enter the Founding cohort with lifetime higher rates.
          </p>
          <Link
            to="/ambassadors/apply"
            className="mt-10 inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/85 px-10 py-5 rounded-sm uppercase tracking-[0.2em] text-xs font-semibold transition"
            data-testid="footer-apply-btn"
          >
            Begin Your Application <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-12 text-xs text-foreground/55">
            Questions? <a href="mailto:ambassadors@thecauldron.app" className="text-secondary hover:underline">ambassadors@thecauldron.app</a>
          </div>
        </div>
      </section>
    </div>
  );
}
